/**
 * Meddelande om ändring av hyresvillkor — införande av förhandlingsklausul
 * enligt 12 kap. 54 § jordabalken.
 *
 * Sibling of `andring_hyresvillkor` whose proposed change is pre-printed
 * legal text instead of free text. The InfoTable and the tillägg's opening
 * are the generic variant's blocks, reused unchanged.
 *
 * Legal text verbatim from `base-templates/Mall_meddelande om ändring av
 * hyresvillkor - införande av förhandlingsklausul.docx` (Haymanot-approved).
 * DO NOT paraphrase or reorder.
 */
import { em, nodes, option, optionList, p, section, strong, t } from '@hyranai/regta-document-ir';
import { dateVal, fieldRows, sectionPlacement, signingBlockDefinition, textVal } from '../common/index.js';
import { NOTICE_SLOTS, effectiveFromParagraph, noticeParagraph, responseParagraph } from '../andring_hyresvillkor/blocks.js';
import { letterOpening } from '../andring_hyresvillkor/kit.js';
const SOURCE = 'base-templates/Mall_meddelande om ändring av hyresvillkor - införande av förhandlingsklausul.docx';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: SOURCE },
};
/** Always ticked — the only alternative the approved form offers. */
const tillaggBox = () => optionList([option(true, 'Bifogade tillägg till hyresavtalet (i övrigt oförändrade villkor)', { code: 'bifogade_tillagg' })], {
    select: 'single',
});
export const rubrik = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor_forhandlingsklausul.rubrik',
    version: 'v1',
    title: 'Rubrik',
    summary: 'Opening of the meddelande: object type and title with statute.',
    slots: [],
    compile: () => letterOpening({
        kicker: 'Bostadslägenhet',
        title: 'Meddelande om ändring av hyresvillkor: införande av förhandlingsklausul enligt 12 kap. 54 § jordabalken',
    }),
};
export const meddelande = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor_forhandlingsklausul.meddelande',
    version: 'v1',
    title: 'Meddelande',
    summary: 'Notice that the landlord proposes to introduce a förhandlingsklausul, with the response deadline.',
    slots: NOTICE_SLOTS.map((s) => (s.key === 'effective_date' ? { ...s, label: 'Förhandlingsklausulen införs fr.o.m.' } : s)),
    compile: (input, ctx) => [
        section('meddelande', 'Meddelande', [
            noticeParagraph(input, ctx),
            p('De ändrade villkoren avser införande av förhandlingsklausul.'),
            p('Förhandlingsklausulen föreslås ändras enligt följande:'),
            tillaggBox(),
            responseParagraph(input, ctx),
        ]),
    ],
};
/** The meddelande's signing section: place and date, landlord name and namnförtydligande above the panel. */
export const underskriftMeddelande = signingBlockDefinition({
    blockKey: 'andring_hyresvillkor_forhandlingsklausul.underskrift-meddelande',
    title: 'Underskrift av meddelandet',
    legalReview: LEGAL.legalReview,
    slots: [
        { key: 'signing_city', label: 'Ort', type: 'text', required: true, help: 'T.ex. Stockholm' },
        { key: 'signing_date', label: 'Datum', type: 'date', required: true },
        {
            key: 'landlord_signatory_name',
            label: 'Firmatecknare / ombud (namnförtydligande)',
            type: 'text',
            help: 'Namn på den person som undertecknar för hyresvärdens räkning.',
        },
    ],
    prelude: (input, ctx) => nodes(p([strong('Ort och datum:'), t(' '), textVal(input, 'signing_city'), t(', '), dateVal(ctx, input, 'signing_date')]), fieldRows([
        ['Hyresvärdens namn', [textVal(input, 'landlord_name')]],
        ['Namnförtydligande', [textVal(input, 'landlord_signatory_name')]],
    ])),
});
export const underskrift = signingBlockDefinition({
    blockKey: 'andring_hyresvillkor_forhandlingsklausul.underskrift',
    title: 'Underskrift av tillägget',
    legalReview: LEGAL.legalReview,
});
export const andradeHyresvillkor = {
    ...LEGAL,
    blockKey: 'andring_hyresvillkor_forhandlingsklausul.andrade-hyresvillkor',
    version: 'v1',
    title: 'Ändrade hyresvillkor',
    summary: 'The pre-printed förhandlingsklausul, its effective date, and that the lease otherwise continues unchanged.',
    slots: [],
    compile: (input, ctx) => {
        const { ref, title, opts } = sectionPlacement(input, {
            ref: 'andrade-hyresvillkor',
            title: 'Ändrade hyresvillkor',
            keepTogether: false,
        });
        return [
            section(ref, title, [
                tillaggBox(),
                p([
                    em('”Förhandlingsklausul gäller för hyresavtalet, vilket innebär att hyran för lägenheten förhandlas fram med den hyresgästorganisation som är part i den för huset gällande förhandlingsordningen.”'),
                ]),
                effectiveFromParagraph(input, ctx),
                p('Hyresavtalet föreslås fortsätta gälla i övrigt på oförändrade villkor.'),
            ], opts),
        ];
    },
};
export const ANDRING_HYRESVILLKOR_FORHANDLINGSKLAUSUL_BLOCKS = [
    rubrik,
    meddelande,
    underskriftMeddelande,
    underskrift,
    andradeHyresvillkor,
];
//# sourceMappingURL=blocks.js.map